/* eslint-disable @typescript-eslint/no-unsafe-return,  @typescript-eslint/no-unused-vars */
/* eslint-disable no-use-before-define, max-len, class-methods-use-this, object-curly-newline */
import { pannaAuth } from '@panna/auth';
import Axios, {
  AxiosInstance,
  AxiosResponse,
} from 'axios';
import { confirmWrapper } from 'src/components/Dialog';
import { Consultancy } from 'src/hooks/query/consultancies/types';
import { ApplyJobRequestBody, JobBooleanStringRespose, JobOption } from 'src/hooks/query/jobs/types';
import { Recruiter } from 'src/hooks/query/recruiters/types';
import { InterviewPositionType } from 'src/hooks/query/interviewPositions/types';
import { BookmarkedCandidateProfilesResponse, BookmarkProfileRequestBody, TalentPoolProfilesRequestBody, TalentPoolResponse } from 'src/pages/TalentPool/types';
import { CandidateIdType } from 'src/context/types';
import {
  AppliedJobsResponse,
  GetJobHistoryRequest,
  JobHistoryResponse,
  UpdateJobStatusRequest,
} from 'src/pages/CandidateAppliedJobs/types';
import { DocumentResponse, UploadDocResponse } from 'src/pages/CandidateDocuments/types';
import { CandidateDetails, CandidateUpdateRequest } from 'src/pages/CandidateInfo/types';
import { CheckDuplicateJobRequest, JobListValuesTypes, SendInternalReferenceLinkProps, UpdateInterviewPositionRequest, UpdateJobsStatusRequest } from 'src/pages/JobRequisition/types';
import { ShareProfilesRequestBody } from 'src/hooks/query/shareProfiles/types';
import { RejectSignatureRequest, SubmitSignatureRequest } from 'src/pages/AuthorizationForm/types';
import { UserByRoleRequest } from 'src/hooks/query/usersByRole/types';
import { MailMergeTemplate, SendBulkMailRequestBody } from 'src/hooks/query/mailMerge/types';
import { MailTemplateRequest, ScheduleLiveRequest, SchedulePannaRequest } from 'src/components/StatusDropdown/types';
import { AllSlotsProps, CreateInterviewSlotsRequest, DaySlotRequest, FetchMonthSlotsRequest, RescheduleInterviewRequest, SlotsProps } from 'src/pages/InterviewSlots/types';
import { FileValueType } from 'src/components/elements/File';
import { JDMatchScoreRequest } from 'src/pages/ViewJob/types';
import { UpdateInterviewStatus } from 'src/pages/CurrentInterviews/types';
import { OpenJob } from 'src/pages/Careers/types';
import { CandidateApplyRequest, JobDetails } from 'src/pages/JobDetail/types';
import { AssessmentData } from 'src/pages/CandidateAssessments/types';
import { FetchCandidatesRequestBody } from 'src/pages/Dice/types';
import { DiceProfile, DiceSearchFetchCanidateRequest, DiceSearchResponse } from 'src/pages/DiceSearch/types';
import { AddATSPropType, LocationFormValues, LocationResponse, UpdateATSPropType } from 'src/pages/Locations/types';
import { SkillResponse } from 'src/pages/Skills/types';
import { DiceDashboardRequest, DiceDashboardResponse } from 'src/pages/DiceDashboard/types';
import { ATSDashboardRequest, ATSDashboardResponse } from 'src/pages/ATSDashboard/types';
import { JobTitleResponse } from 'src/pages/CandidateJobTitles/types';
import {
  candidateProfile,
  documents,
  common,
  jobs,
  viewJob,
  talentpool,
  signatureForm,
  interviews, postJob,
  interviewSlots,
  resumeParser,
  candidate,
  terms,
  consultancy,
  dice,
  ats,
} from './configuration';
import Accounts from './accounts';
import { getCookie } from './utils';

/**
 * Requests
 */
class Requests {
  static client: Requests;

  axios: AxiosInstance;

  constructor() {
    this.axios = Axios.create({
      baseURL: process.env.REACT_APP_SERVICES_API,
    });
    this.axios.defaults.withCredentials = false;
    this.axios.defaults.timeout = 35000;

    const token = getCookie('token');
    if (token) {
      this.setBearerToken(token);
    }
    this.axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response && error.response.status === 401) {
          await confirmWrapper('Session expired. Redirecting to login page...', {
            hidePrimaryBtn: true,
            hideSecondaryBtn: true,
            autoClose: true,
            variant: 'warning',
          });
          await pannaAuth.signOut({ redirectToLogin: true });
          // console.log('Unauthorized request. Redirecting to login page...');
          // Redirect to login page or perform any other action
        }
        return Promise.reject(error);
      },
    );
  }

  setBearerToken = (token: string) => {
    this.axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  };

  static getInstance = () => {
    if (!Requests.client) {
      Requests.client = new Requests();
    }
    return Requests.client;
  };

  getClients = () => Accounts.getInstance().getClients();

  getSources = () => this.axios.get<string[]>(common.sources);

  getJobtitles = () => Accounts.getInstance().getJobtitles();

  getUsersByRole = (payload: UserByRoleRequest) => Accounts.getInstance().getUsersByRoles(payload);

  getRecruiters = () => this.axios.get<Recruiter[]>(jobs.recruiters);

  getLocations = () => this.axios.get<string[]>(common.locations);

  getJobs = () => this.axios.get<JobOption[]>(common.jobIds);

  getActiveJobIds = () => this.axios.get<JobOption[]>(common.activeJobIds);

  getMailMergeTemplate = (jobOrderId: number) => this.axios.get<MailMergeTemplate>(`${common.getMailMergeTemplate}${jobOrderId}`);

  sendBulkMail = (data: SendBulkMailRequestBody) => this.axios.post(talentpool.bulkMail, data);

  applyJob = (data: ApplyJobRequestBody) => this.axios.post(talentpool.applyJob, data);

  shareProfiles = (data: ShareProfilesRequestBody) => this.axios.post(talentpool.shareCandidateProfiles, data);

  jobDetails = (jobOrderId: string) => this.axios.get(jobs.editJob + jobOrderId)
    .then((response: AxiosResponse<any>) => response.data);

  fetchJobDetails = (jobOrderId: string) => this.axios.get(jobs.fetchJobDetails + jobOrderId)
    .then((response: AxiosResponse<any>) => response.data);

  updateJobDescription = (request: any) => this.axios.post(jobs.updateDescription, request);

  getSkills = () => this.axios.get<string[]>(common.skills);

  getStatuses = () => this.axios.get<string[]>(common.statuses);

  getMailTemplateList = () => this.axios.get<string[]>(common.mailTemplateList);

  getMailTemplate = (request: MailTemplateRequest) => this.axios.post<any>(common.getMailTemplate, request)
    .then((response: AxiosResponse<any>) => response.data);

  sendEmail = (request: any) => this.axios.post<any>(common.sendEmail, request);

  updateCandidateStatus = (request: any) => this.axios.post<any>(viewJob.updateCandidateStatus, request);

  updateJobDetails = (request: any) => this.axios.post<any>(jobs.updateJobDetails, request);

  updateOthersTab = (request: any) => this.axios.post<any>(jobs.updateOthersTab, request);

  updateJobLocations = (request: any) => this.axios.post<any>(jobs.updateJobLocation, request);

  updateJobRecruiters = (request: any) => this.axios.post<any>(jobs.updateJobRecruiters, request);

  updateSkills = (request: any) => this.axios.post<any>(jobs.updateSkills, request);

  addJobNotes = (request: any) => this.axios.post(jobs.addJobNotes, request)
    .then((response: AxiosResponse<any>) => response.data);

  updateJobNotes = (request: any, id: number) => this.axios.post(jobs.updateJobNotes + id, request)
    .then((response: AxiosResponse<any>) => response.data);

  getInterviewPositions = () => this.axios.get<InterviewPositionType[]>(postJob.getInterviewPositions);

  createJob = (data: FormData) => this.axios.post(jobs.createJob, data)
    .then((response: AxiosResponse<{ message: string }>) => response.data);

  getConsultancies = () => this.axios.get<Consultancy[]>(common.consultancies);

  filterJobs = (payload: any) => this.axios.post(jobs.filterJobs, payload)
    .then((response: AxiosResponse<JobListValuesTypes[]>) => response.data);

  getAllCandidatesForJob = (jobOrderId: string) => this.axios.get(viewJob.getAllCandidatesForJob + jobOrderId)
    .then((response: AxiosResponse<any>) => response.data);

  getCandidatesOfRecruiter = (jobOrderId: string) => this.axios.get(viewJob.getCandidatesOfRecruiter + jobOrderId)
    .then((response: AxiosResponse<any>) => response.data);

  getTalentPoolProfiles = (payload: TalentPoolProfilesRequestBody) => this.axios.post(talentpool.candidateProfiles, payload)
    .then((response: AxiosResponse<TalentPoolResponse>) => response.data);

  bookmarkCandidateProfile = (payload: BookmarkProfileRequestBody) => this.axios.post(talentpool.bookmark, payload)
    .then((response: AxiosResponse) => response.data);

  getBookmarkedCandidateProfiles = () => this.axios.get(talentpool.bookmarkRecords)
    .then((data: AxiosResponse<BookmarkedCandidateProfilesResponse>) => data.data);

  postJobOnWebSite = (jobOrderId: number) => this.axios.patch(postJob.postJobOnWebSite + jobOrderId)
    .then((response: AxiosResponse<string>) => response.data);

  getCurrentInterviews = (payload: any) => this.axios.post(interviews.getCurrentInterviews, payload)
    .then((response: AxiosResponse<any>) => response.data);

  getCompletedInterviews = (payload: any) => this.axios.post(interviews.getCompletedInterviews, payload)
    .then((response: AxiosResponse<any>) => response.data);

  schedulePanna = (payload: SchedulePannaRequest) => this.axios.post(interviews.schedulePanna, payload)
    .then((response: AxiosResponse<any>) => response.data);

  scheduleLive = (payload: ScheduleLiveRequest) => this.axios.post(interviews.scheduleLive, payload)
    .then((response: AxiosResponse<any>) => response.data);

  updateInterviewStatus = (payload: UpdateInterviewStatus) => this.axios.post(interviews.updateInterviewStatus, payload)
    .then((response: AxiosResponse<any>) => response.data);

  updateInterviewPosition = (payload: UpdateInterviewPositionRequest) => this.axios.patch(postJob.updateInterviewPosition, payload)
    .then((response: AxiosResponse<string>) => response.data);

  updateJobsStatus = (payload: UpdateJobsStatusRequest) => this.axios.post(jobs.updateJobStatus, payload)
    .then((response: AxiosResponse<string>) => response.data);

  shareInternalLink = (payload: SendInternalReferenceLinkProps) => this.axios.post(jobs.shareInternalLink, payload)
    .then((response: AxiosResponse<string>) => response.data);

  fetchCandidateInfo = (id: CandidateIdType) => this.axios.get(candidateProfile.fetchCandidateInfo + id).then(
    (response: AxiosResponse<CandidateDetails>) => response.data,
  );

  getCandidateDocuments = (candidateId: CandidateIdType) => this.axios.get(candidateProfile.getCandidateDocuments + candidateId)
    .then((response: AxiosResponse<DocumentResponse[]>) => response.data);

  documentUpload = (id: any, data: FormData) => this.axios.post(documents.uploadDocument + id, data)
    .then((response: AxiosResponse<UploadDocResponse>) => response.data);

  getAllPaytypes = () => this.axios.get(candidateProfile.getAllPayTypes).then(
    (response: AxiosResponse<any>) => response.data,
  );

  updateProfileInfo = (id: CandidateIdType, data: CandidateUpdateRequest) => this.axios.post(candidateProfile.updateProfileInfo + id, data)
    .then((response: AxiosResponse<string>) => response.data);

  fetchAppliedJobs = (id: CandidateIdType) => this.axios.get(candidateProfile.fetchAppliedJobs + id).then(
    (response: AxiosResponse<AppliedJobsResponse>) => response.data,
  );

  getCandidateJobHistory = (data: GetJobHistoryRequest) => this.axios.post(candidateProfile.getCandidateJobHistory, data).then(
    (response: AxiosResponse<JobHistoryResponse>) => response.data,
  );

  getAllJobStatuses = () => this.axios.get(candidateProfile.getAllJobStatuses).then(
    (response: AxiosResponse<any>) => response.data,
  );

  updateJobStatus = (data: UpdateJobStatusRequest) => this.axios.post(candidateProfile.updateJobStatus, data)
    .then((response: AxiosResponse<string>) => response.data);

  getAllJobTypes = () => this.axios.get(candidateProfile.getAllJobTypes).then(
    (response: AxiosResponse<any>) => response.data,
  );

  getSignatureForm = (id: string) => this.axios.get(signatureForm.getSignatureForm + id)
    .then((response: AxiosResponse<any>) => response.data);

  submitFeedback = (data: RejectSignatureRequest) => this.axios.post(signatureForm.submitFeedback, data)
    .then((response: AxiosResponse<string>) => response.data);

  submitSignature = (payload: SubmitSignatureRequest) => this.axios.post(signatureForm.submitSignature, payload)
    .then((response: AxiosResponse<string>) => response.data);

  getAllWorkAuthorizations = () => this.axios.get(candidateProfile.getAllWorkAuthorizations).then(
    (response: AxiosResponse<any>) => response.data,
  );

  getAllResumeSources = () => this.axios.get(candidateProfile.getAllResumeSources).then(
    (response: AxiosResponse<any>) => response.data,
  );

  getMonthSlots = (payload: FetchMonthSlotsRequest) => this.axios.post(interviewSlots.monthSlots, payload).then(
    (response: AxiosResponse<AllSlotsProps>) => response.data,
  );

  createInterviewSlots = (payload: CreateInterviewSlotsRequest) => this.axios.post(interviewSlots.createInterviewSlots, payload).then(
    (response: AxiosResponse<string>) => response.data,
  );

  deleteInterviewSlots = (id: number) => this.axios.delete(interviewSlots.deleteInterviewSlots + id).then(
    (response: AxiosResponse<string>) => response.data,
  );

  getAllSlots = () => this.axios.get(interviewSlots.getAllSlots).then(
    (response: AxiosResponse<AllSlotsProps>) => response.data,
  );

  rescheduleInterviewSlot = (payload: RescheduleInterviewRequest) => this.axios.post(interviewSlots.rescheduleInterviewSlot, payload).then(
    (response: AxiosResponse<string>) => response.data,
  );

  getDaySlots = (payload: DaySlotRequest) => this.axios.post(interviewSlots.getDaySlots, payload)
    .then((response: AxiosResponse<Array<SlotsProps>>) => response.data);

  checkEmailDuplicate = (email: string) => this.axios.get(resumeParser.checkEmailDuplicate + email).then(
    (response: AxiosResponse<any>) => response.data,
  );

  onCandidateDetailsSave = (request: any) => this.axios.post(resumeParser.onCandidateDetailsSave, request).then(
    (response: AxiosResponse<any>) => response.data,
  );

  getPreferredJobLocations = (id: CandidateIdType) => this.axios.get<string[]>(common.preferredJobLocations + id);

  updatePlacedWithMroads = (request: any) => this.axios.post<any>(viewJob.updatePlacedWithMroads, request);

  getMatchingResumes = (request: any) => this.axios.get(viewJob.getMatchingResumes + request).then(
    (response: AxiosResponse<any>) => response.data,
  );

  updateStatusComments = (request: any) => this.axios.post<any>(viewJob.updateCandidateComments, request);

  jdMatchScore = (data: JDMatchScoreRequest) => this.axios.post(resumeParser.jdMatchScore, data).then(
    (response: AxiosResponse<any>) => response.data,
  );

  checkDuplicateJobId = (data: CheckDuplicateJobRequest) => this.axios.post(jobs.checkDuplicateJobId, data).then(
    (response: AxiosResponse<any>) => response,
  );

  getCareersByOrgId = (orgId: number) => this.axios.get(common.getJobByOrgId + orgId).then(
    (response: AxiosResponse<OpenJob[]>) => response.data,
  );

  getJobDetailsByJobOrderId = (jobOrderId: number) => this.axios.get(common.getJobDetailsByJobOrderId + jobOrderId).then(
    (response: AxiosResponse<JobDetails>) => response.data,
  );

  fetchCandidateResumeDetails = (candidateId: string | number) => this.axios.get(candidateProfile.fetchCandidateResumeDetails + candidateId).then(
    (response: AxiosResponse<any>) => response,
  );

  uploadResume = (data: FormData) => this.axios.post('https://commonservices-prod.mroads.com/commonservices/api/resume-upload-parse-service', data).then(
    (response: AxiosResponse<any>) => response.data,
  );

  updateResume = (data: any) => this.axios.post(candidateProfile.updateResume, data).then(
    (response: AxiosResponse<any>) => response.data,
  );

  candidateApply = (data: CandidateApplyRequest) => this.axios.post(candidate.candidateApply, data).then(
    (response: AxiosResponse<any>) => response.data,
  );

  referAJob = (data: any) => this.axios.post('https://commonservices-prod.mroads.com/commonservices/job/referAJob/', data).then(
    (response: AxiosResponse<any>) => response.data,
  );

  saveAudio = (data: FormData) => this.axios.post(common.saveAudio, data).then(
    (response: AxiosResponse<any>) => response.data,
  );

  diceJob = (data: FetchCandidatesRequestBody) => this.axios.post(common.diceJob, data).then(
    (response: AxiosResponse<any>) => response.data,
  );

  // Creers assesments

  startAssesment = (data: { resume_path: string, jd_text: string, jobOrderId: string, candidate_id: string }) => this.axios.post('https://ats-screening.mroads.com/start_interview', data).then(
    (response: AxiosResponse<{ session_id: string }>) => response.data,
  );

  generateQuestion = (data: { session_id: string }) => this.axios.post('https://ats-screening.mroads.com/generate_question', data).then(
    (response: AxiosResponse<{
      question: string;
      input_tokens: number;
      output_tokens: number;
    }>) => response.data,
  );

  transcribeAudio = (data: { session_id: string; audio_file_path: string }) => this.axios.post('https://ats-screening.mroads.com/transcribe_audio', data).then(
    (response: AxiosResponse<{ transcribed_text: string }>) => response.data,
  );

  endAssessment = (data: { session_id: string }) => this.axios.post('https://ats-screening.mroads.com/end_assessment', data).then(
    (response: AxiosResponse<{
      evaluation_score: number;
      evaluation_feedback_message: string;
      input_tokens: number;
      output_tokens: number;
    }>) => response.data,
  );

  submitUserFeedback = (data: { session_id: string; user_feedback: string; total_time: string }) => this.axios.post('https://ats-screening.mroads.com/submit_user_feedback', data).then(
    (response: AxiosResponse<{ message: string }>) => response.data,
  );

  saveFileToS3 = (formData: FormData) => this.axios.post('https://commonservices-prod.mroads.com/commonservices/api/uploadFileToS3', formData).then(
    (response: AxiosResponse<any>) => response.data,
  );

  fetchAssessment = (data: { candidateId: string; jobOrderId: string }) => this.axios.post('https://ats-screening.mroads.com/fetch_candidate_assessment2', data).then(
    (response: AxiosResponse<AssessmentData>) => response.data,
  );

  fetchOrgTermsById = (organizationId: number) => this.axios.get(terms.fetchOrgTermsById + organizationId).then(
    (response: AxiosResponse<any>) => response.data,
  );

  diceSearch = (payload: DiceSearchFetchCanidateRequest, abortController?: AbortController | null) => this.axios.post(dice.searchProfiles, payload, {
    signal: abortController?.signal,
  }).then(
    (response: AxiosResponse<DiceSearchResponse>) => response.data,
  );

  diceViewProfile = (payload: { id: string, candidateId: string, jobId: number }) => this.axios.post(dice.viewProfile, payload).then(
    (response: AxiosResponse<DiceProfile>) => response.data,
  );

  // settings
  fetchLocations = () => this.axios.get<LocationResponse[]>(common.fetchLocations);

  fetchSkills = () => this.axios.get<SkillResponse[]>(common.fetchSkills);

  fetchJobTitles = () => this.axios.get<JobTitleResponse[]>(common.fetchJobTitles);

  addATSProperty = (data: AddATSPropType) => this.axios.post(common.addATSProperty, data).then(
    (response: AxiosResponse<string>) => response.data,
  );

  updateATSProperty = (data: UpdateATSPropType) => this.axios.post(common.updateATSProperties, data).then(
    (response: AxiosResponse<string>) => response.data,
  );

  deleteATSProperties = (id: number) => this.axios.patch(common.deleteATSProperties + id).then(
    (response: AxiosResponse<string>) => response.data,
  );

  getResumeSearchString = (jobOrderId: number) => this.axios.get<JobBooleanStringRespose>(`${jobs.getResumeSearchString}${jobOrderId}`).then(
    (response: AxiosResponse<JobBooleanStringRespose>) => response.data,
  );

  getDiceDashboardData = (payload: DiceDashboardRequest) => this.axios.post(dice.diceDashboard, payload).then(
    (response: AxiosResponse<DiceDashboardResponse>) => response.data,
  );

  updateATSViewFromDice = (payload: { hashKey: string }) => this.axios.post(dice.atsViewProfile, payload).then(
    (response: AxiosResponse<string>) => response.data,
  );

  getDiceProfileHistory = (params: { page: number; pageSize: number; startDate?: string; endDate?: string }) => this.axios.post('/dice/interactionHistory', { ...params }).then(
    (response: AxiosResponse<any>) => response.data,
  );

  getATSDashboardData = (payload: ATSDashboardRequest) => this.axios.post(ats.atsDashboard, payload).then(
    (response: AxiosResponse<ATSDashboardResponse>) => response.data,
  );
}

export default Requests;
